import React from "react";
import { Link } from "react-router-dom";
import { Users, ArrowUpRight, Luggage } from "lucide-react";
import { TID } from "../lib/testIds";
import VehicleGallery from "./VehicleGallery";

const AVAILABILITY = {
  available: { label: "AVAILABLE", cls: "bg-emerald-400 text-emerald-950 shadow-[0_0_16px_rgba(52,211,153,0.6)] font-bold", dot: "bg-emerald-950" },
  limited:   { label: "LIMITED",   cls: "bg-amber-400 text-amber-950 shadow-[0_0_16px_rgba(251,191,36,0.6)] font-bold",   dot: "bg-amber-950" },
  sold_out:  { label: "SOLD OUT",  cls: "bg-red-500 text-white shadow-[0_0_16px_rgba(239,68,68,0.6)] font-bold",           dot: "bg-white" },
};

export default function VehicleCard({ vehicle, searchStart, searchEnd }) {
  const canRide = vehicle.available_for === "chauffeur" || vehicle.available_for === "both";
  const canRent = vehicle.available_for === "rental" || vehicle.available_for === "both";
  const avail = AVAILABILITY[vehicle.availability] || AVAILABILITY.available;
  const rentalQs = (searchStart && searchEnd)
    ? `?start=${encodeURIComponent(searchStart)}&end=${encodeURIComponent(searchEnd)}`
    : "";

  const overlay = (
    <>
      <div className="absolute top-3 left-3 flex gap-2 flex-wrap z-10 pointer-events-none">
        {canRide && (
          <span className="label-caps px-2.5 py-1 rounded-full bg-black/70 border border-cream/40 text-cream">Chauffeur</span>
        )}
        {canRent && (
          <span className="label-caps px-2.5 py-1 rounded-full bg-black/70 border border-platinum/40 text-platinum">Rental</span>
        )}
        {vehicle.rideshare_ready && (
          <span className="label-caps px-2.5 py-1 rounded-full bg-black/70 border border-cream/30 text-cream/80">Rideshare</span>
        )}
      </div>
      <div className="absolute top-3 right-3 z-10 pointer-events-none">
        <span className={`label-caps inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-widest ${avail.cls}`}
              data-testid={`vehicle-availability-${vehicle.id}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${avail.dot}`} />
          {avail.label}
        </span>
      </div>
      <div className="absolute bottom-3 right-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/70 border border-white/10 text-xs z-10 pointer-events-none">
        <Users size={12} className="text-white/70" /> <span>{vehicle.capacity}</span>
        {vehicle.luggage_capacity ? <><span className="text-white/30">·</span>
          <Luggage size={12} className="text-white/70" /> <span>{vehicle.luggage_capacity}</span></> : null}
      </div>
    </>
  );

  return (
    <div
      data-testid={TID.fleet.card(vehicle.id)}
      className="group relative overflow-hidden rounded-2xl border border-white/8 bg-ink-800/70 hover:border-cream/40 transition-colors"
    >
      <VehicleGallery
        images={vehicle.images}
        image_url={vehicle.image_url}
        alt={vehicle.name}
        aspect="aspect-[16/10]"
        enableLightbox={true}
        overlayChildren={overlay}
        testId={`vehicle-card-gallery-${vehicle.id}`}
      />

      <div className="p-5 space-y-4">
        <Link to={`/vehicles/${vehicle.id}`}>
          <div className="label-caps text-white/40 mb-1">{vehicle.category.replace(/-/g, " ")}</div>
          <h3 className="font-serif text-xl leading-tight hover:text-cream transition-colors">{vehicle.name}</h3>
        </Link>
        <p className="text-sm text-white/60 leading-relaxed line-clamp-2">{vehicle.description}</p>

        <div className="flex items-end justify-between pt-2">
          <div>
            {canRide && vehicle.hourly_rate > 0 && (
              <div className="text-xs text-white/50">
                <span className="text-cream font-semibold text-base">${vehicle.hourly_rate.toFixed(0)}</span>/hr chauffeured
              </div>
            )}
            {canRent && (
              <div className="text-xs text-white/50">
                <span className="text-platinum font-semibold text-base">${vehicle.daily_rate.toFixed(0)}</span>/day
                {vehicle.weekly_rate && <span className="text-white/40"> · ${vehicle.weekly_rate.toFixed(0)}/wk</span>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {canRide && (
              <Link data-testid={TID.fleet.bookRide(vehicle.id)}
                to={`/book/ride/${vehicle.id}`}
                className="btn-cream px-3.5 py-2 rounded-full text-xs inline-flex items-center gap-1">
                Ride <ArrowUpRight size={12} />
              </Link>
            )}
            {canRent && (
              <Link data-testid={TID.fleet.bookRental(vehicle.id)}
                to={`/book/rental/${vehicle.id}${rentalQs}`}
                className="btn-outline-platinum px-3.5 py-2 rounded-full text-xs inline-flex items-center gap-1 hover:text-white">
                Rent <ArrowUpRight size={12} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
